import React, { useMemo, useState } from 'react';

import {
  Box,
  Collapse,
  FormControlLabel,
  IconButton,
  Slider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon, SettingsIcon } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

type MarketDataSettingsProps = {
  cmaReport: {
    market_data_show_map?: boolean | null;
    market_data_show_chart?: boolean | null;
    market_data_map_zoom_level?: number | null;
  };
  onUpdate: (
    field:
      | 'market_data_show_map'
      | 'market_data_show_chart'
      | 'market_data_map_zoom_level',
    value: boolean | number,
  ) => void;
};

const MarketDataSettings = ({
  cmaReport,
  onUpdate,
}: MarketDataSettingsProps) => {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(false);

  const showMap = !!cmaReport.market_data_show_map;
  const showChart = !!cmaReport.market_data_show_chart;
  const zoomLevel = cmaReport.market_data_map_zoom_level ?? 14;

  const enabledCount = useMemo(
    () => Number(showMap) + Number(showChart),
    [showMap, showChart],
  );

  return (
    <Box
      sx={{
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        flex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 0.5,
          px: 2,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
          }}
        >
          <SettingsIcon
            style={{ flexShrink: 0 }}
            size={20}
            color={'var(--mui-palette-text-secondary)'}
          />
          <Typography
            variant="subtitle2"
            noWrap
            sx={{
              fontWeight: 600,
            }}
          >
            {t('Settings')}
          </Typography>
          {!expanded && enabledCount > 0 && (
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              •{' '}
              {enabledCount === 2
                ? t('Map & Chart')
                : showMap
                ? t('Map')
                : t('Chart')}
            </Typography>
          )}
        </Stack>
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? (
            <ChevronUpIcon size={20} />
          ) : (
            <ChevronDownIcon size={20} />
          )}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ maxWidth: 350 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showMap}
                  onChange={e =>
                    onUpdate('market_data_show_map', e.target.checked)
                  }
                />
              }
              label={t('Display price map in report')}
              sx={{ whiteSpace: 'normal' }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={showChart}
                  onChange={e =>
                    onUpdate('market_data_show_chart', e.target.checked)
                  }
                />
              }
              label={t('Display price evolution chart in report')}
              sx={{ whiteSpace: 'normal' }}
            />

            {showMap && (
              <Box sx={{ px: 1 }}>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('Zoom level')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {zoomLevel}
                  </Typography>
                </Stack>
                <Slider
                  value={zoomLevel}
                  onChange={(_, value) =>
                    onUpdate('market_data_map_zoom_level', value as number)
                  }
                  min={10}
                  max={18}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default MarketDataSettings;
